import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import axios from 'axios';
import { Calendar, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/news/${id}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Error fetching article:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchArticle();
    }
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500 text-xl">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-xl mb-4">
            {language === 'de' ? 'Artikel nicht gefunden' : 'Article not found'}
          </p>
          <button
            onClick={() => navigate('/news')}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
          >
            {language === 'de' ? 'Zurück zu News' : 'Back to News'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/news')}
            className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {language === 'de' ? 'Zurück zu News' : 'Back to News'}
          </button>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Image */}
          {article.image_url && (
            <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
              <img 
                src={article.image_url} 
                alt={language === 'de' ? article.title_de : article.title_en}
                className="w-full h-96 object-cover"
              />
            </div>
          )}

          {/* Article Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="inline-block bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold uppercase">
                {article.category}
              </span>
              <div className="flex items-center text-gray-500">
                <Calendar className="w-5 h-5 mr-2" />
                <span>{formatDate(article.created_at)}</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {language === 'de' ? article.title_de : article.title_en}
            </h1>

            {/* Excerpt */}
            {(article.excerpt_de || article.excerpt_en) && (
              <div className="text-xl text-gray-600 mb-6 pb-6 border-b border-gray-200 italic">
                {language === 'de' ? article.excerpt_de : article.excerpt_en}
              </div>
            )}

            {/* Main Content */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {language === 'de' ? article.content_de : article.content_en}
              </div>
            </div>
          </div>

          {/* Share/Actions Section (Optional) */}
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <p className="text-gray-600 mb-4">
              {language === 'de' ? 'Interessiert an unseren Kursen?' : 'Interested in our courses?'}
            </p>
            <a
              href="/contact"
              className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              {language === 'de' ? 'Vorab-Registrierung' : 'Pre-Registration'}
            </a>
          </div>
        </div>
      </article>
    </div>
  );
};

export default NewsDetail;
